import { createContext, useContext } from 'react';

/**
 * Scope provided by {@link RightSidebar.Drawer} (the mobile drawer) to the panel
 * subtree it hosts. Lets a panel's {@link RightSidebar.PanelHeader} own the close
 * affordance and lets panels dismiss the drawer after acting — without any panel
 * taking a drawer-specific prop, so the same panels render unchanged on desktop
 * (where no provider is mounted and the hook returns `undefined`).
 */
export interface RightSidebarDrawerContextValue {
  /** Dismiss the hosting drawer. */
  close: () => void;
  /**
   * Whether the panel header should render the close button. True for the
   * single-tab drawer (no rail strip); false when the rail strip already shows
   * an `X` (2+ tabs), so the header stays clean.
   */
  headerShowsClose: boolean;
}

const ctx = createContext<RightSidebarDrawerContextValue | undefined>(
  undefined,
);
ctx.displayName = 'RightSidebarDrawer';

export const RightSidebarDrawerProvider = ctx.Provider;

/**
 * Returns the hosting drawer's scope, or `undefined` when not inside a
 * {@link RightSidebar.Drawer} (e.g. the desktop rail/panel).
 */
export const useRightSidebarDrawer = () => useContext(ctx);
